import { LoadingContainer } from 'components/Loading'
import { useMedia } from 'helpers/hooks/useMedia'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { getImageUrl } from 'helpers/image'
import { buildGenericStoryImageAlt } from 'helpers/seo'
import { useObserver } from 'mobx-react-lite'
import React, { ComponentType } from 'react'
import BackButton from 'sharedComponents/BackButton/BackButton'
import ProductStreamFooter from 'sharedComponents/ProductStream/ProductStreamFooter'
import ProductStreamHeaderContent from 'sharedComponents/ProductStream/ProductStreamHeaderContent'
import ProductStreamLayout from 'sharedComponents/ProductStream/ProductStreamLayout'
import SideNavigation from 'sharedComponents/ProductStream/SideNavigation/SideNavigation'
import ErrorBoundary from 'sharedComponents/Utils/ErrorBoundary'
import { useStoryPageStore } from 'store/context'
import ICategory from 'store/entities/Category/ICategory'
import { ActiveImageTypeContext } from 'store/features/imageView/context'
import { ProductStreamContextProvider } from 'store/sidecar/ProductStreamContext'
import styled from 'styled-components'
import RowSection from 'uiComponents/atoms/Sections/RowSection'
import EditorialHeadline from 'uiComponents/molecules/EditorialHeadline/EditorialHeadline'
import { NavigationTreeNodeContent } from 'uiComponents/molecules/NavigationTree/NavigationTree'
import PageTeaser from 'uiComponents/organisms/PageTeaser/PageTeaser'
import DocumentHead from './components/DocumentHead'
import StoryStream from './components/StoryStream'
import { useTransparentBrandLogo } from './components/hooks/useTransparentBrandLogo'

const StyledRowSection = styled(RowSection)`
  padding-top: 35px;
`

const StyledEditorialHeadline = styled(EditorialHeadline)`
  padding: 25px 25px 50px;
`

const StoryPage: React.FC<{
  ScrollTopAnchor?: ComponentType<any>
  navigationStickyOffset?: number
  stickyHeaderHeight?: number
}> = ({ ScrollTopAnchor, navigationStickyOffset, stickyHeaderHeight }) => {
  const { t } = useTranslation()
  const storyPageStore = useStoryPageStore()
  const streamRef = React.useRef<HTMLElement>(null)
  const storyBasePath = useObserver(() => {
    if (!storyPageStore.story) {
      return undefined
    }
    return storyPageStore.story.url
  })
  const story = useObserver(() => storyPageStore.story)
  const introText = useObserver(() => storyPageStore.introText)
  const logoSize = useMedia(
    {
      '(min-width: 0px)': 500,
      '(min-width: 768px)': 800,
      '(min-width: 1024px)': 1024,
      '(min-width: 1400px)': 1500,
    },
    800,
  )
  const transparentBrandLogo = useTransparentBrandLogo(story, logoSize)
  const isIdolStory = useObserver(() => (story ? story.isIdolStory : false))

  const availableCategoryIds = useObserver(
    () => storyPageStore.category.availableCategoryIds,
  )
  const activeImageType = useObserver(() => storyPageStore.activeImageType)

  const activeCategoryId = useObserver(() => storyPageStore.category.id)

  if (!story) {
    return <LoadingContainer />
  }

  const teaserImg = getImageUrl(story.image, {
    width: 2000,
    height: 2000,
    trim: false,
  })

  const main = (
    <>
      {!isIdolStory && (
        <ErrorBoundary>
          <ProductStreamHeaderContent
            title={story.headline}
            productCurrencyCode={storyPageStore.productCurrencyCode}
            stickyHeaderHeight={stickyHeaderHeight}
          />
        </ErrorBoundary>
      )}
      {introText && <StyledEditorialHeadline title={null} text={introText} />}
      <StoryStream />
      <ProductStreamFooter />
    </>
  )

  const storyCategoryId = `${story.categoryId}`

  const rootCategory: NavigationTreeNodeContent<ICategory> = {
    href: storyBasePath || '',
    data: {
      id: storyCategoryId,
      name: t('Alle Produkte'),
      url: storyBasePath || '',
      active: true,
      hidden: false,
      parentId: null,
      childrenIds: [],
      sustainable: false,
      showProductName: false,
      sizeRuns: undefined,
      disableSizeFilter: false,
      supportedFilter: [],
    },
    current: storyCategoryId === activeCategoryId,
    active: true,
    empty: false,
    campaignColor: undefined,
  }

  const backButton = <BackButton key={'back'} />

  return (
    <ActiveImageTypeContext.Provider value={activeImageType}>
      <PageTeaser
        largeTeaser={story.largeTeaser}
        src={teaserImg!}
        title={story.headline}
        subtitle={story.subline}
        transparentLogoSrc={transparentBrandLogo}
        isPremium={story.isPremium}
        isSponsored={story.isSponsored}
        isSale={story.isSale}
        targetScrollElementRef={streamRef}
        fullscreen={true}
        backButton={backButton}
        alt={buildGenericStoryImageAlt(story.headline, story.subline)}
      />
      {ScrollTopAnchor && <ScrollTopAnchor />}
      <ProductStreamContextProvider
        filterViewStore={storyPageStore.filter}
        paginationViewStore={storyPageStore.pagination}
        sortingViewStore={storyPageStore.sorting}
        categoryViewStore={storyPageStore.category}
        pageType={'story'}
      >
        <DocumentHead />
        {isIdolStory ? (
          <StyledRowSection ref={streamRef}>{main}</StyledRowSection>
        ) : (
          <ProductStreamLayout
            padded
            ref={streamRef}
            navigationStickyOffset={navigationStickyOffset}
            navigation={
              <ErrorBoundary>
                <SideNavigation
                  keepFilterParams
                  disableExpansion
                  rootIds={availableCategoryIds}
                  basePath={storyBasePath}
                  overrideRootCategory={rootCategory}
                  excludeTop100
                />
              </ErrorBoundary>
            }
            main={main}
          />
        )}
      </ProductStreamContextProvider>
    </ActiveImageTypeContext.Provider>
  )
}

export default StoryPage
